// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../utils/gql.ts'
import type { ComandaEspacioProps, EstadoCocinaProps } from '../interfaces/comanda.ts'
import type { TipoPedido } from '../interfaces/mesa.interface.ts'

const query = /* GraphQL */ gqlAst`
  query COMANDAS_COCINA_NOTIFICACION(
    $codigoSucursal: Int!
    $cursor: String
    $estados: [String!]
    $codigoPuntoVenta: Int
    $areas: [String!]
  ) {
    restCocinaNotificaciones(
      codigoSucursal: $codigoSucursal
      cursor: $cursor
      estados: $estados
      codigoPuntoVenta: $codigoPuntoVenta
      areas: $areas
    ) {
      cursor
      notificaciones {
        area
        areaNombre
        comandaId
        espacio {
          _id
          descripcion
        }
        estado
        estadoActual
        estadoAnterior
        fecha
        id
        mesa {
          nombre
        }
        numeroComanda
        numeroOrden
        numeroPedido
        pedidoId
        tipoPedido
        usuario
      }
    }
  }
`

export const KEY_COMANDAS_COCINA_NOTIFICACION = 'comandas-cocina-notificacion'

export interface ApiComandasCocinaNotificacionParams {
  codigoSucursal: number
  /** cursor de la respuesta anterior; null en la primera consulta */
  cursor?: string | null
  /** Estados a notificar; vacío = EN_PREPARACION y LISTO */
  estados?: EstadoCocinaProps[]
  /** Punto de venta a notificar; null = todos los de la sucursal */
  codigoPuntoVenta?: number | null
  /** _id de las áreas (Area); impresora si el artículo no tiene áreas; o GENERAL. [] = todas las áreas */
  areas?: string[]
}

/** Cambio de estado de cocina de un área de una comanda */
export interface ComandaCocinaNotificacionProps {
  /** Identificador de la notificación */
  id: string
  /** _id del área (Area); impresora si el artículo no tiene áreas; o GENERAL */
  area: string
  areaNombre: string
  comandaId: string
  espacio: ComandaEspacioProps
  /** Estado al que pasó el área con este cambio */
  estado: EstadoCocinaProps
  /** Estado del área al momento de consultar (puede haber avanzado después del cambio) */
  estadoActual: EstadoCocinaProps | null
  /** Estado del área antes del cambio */
  estadoAnterior: EstadoCocinaProps | null
  /** Fecha del cambio */
  fecha: string
  mesa: { nombre: string }
  numeroComanda: number
  numeroOrden: number
  numeroPedido: number
  pedidoId: string
  tipoPedido: TipoPedido | null
  /** Usuario que hizo el cambio */
  usuario: string
}

export interface ComandasCocinaNotificacionesProps {
  /** Pasar en la siguiente consulta para recibir solo las notificaciones nuevas */
  cursor: string | null
  notificaciones: ComandaCocinaNotificacionProps[]
}

/**
 * Notificaciones de cambios de estado de cocina (ej. avisar al mesero que un pedido está LISTO).
 * Se consulta periódicamente pasando el cursor de la respuesta anterior para recibir solo lo nuevo.
 * @param params Sucursal, cursor y filtros opcionales (estados, punto de venta, áreas).
 * @param options Headers adicionales y señal de cancelación de la petición.
 */
export const apiComandasCocinaNotificacion = async (
  params: ApiComandasCocinaNotificacionParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ComandasCocinaNotificacionesProps> => {
  try {
    const data = await graphqlClient.request<{ restCocinaNotificaciones: ComandasCocinaNotificacionesProps }>(
      {
        document: query,
        variables: {
          codigoSucursal: params.codigoSucursal,
          cursor: params.cursor ?? null,
          estados: params.estados ?? [],
          codigoPuntoVenta: params.codigoPuntoVenta ?? null,
          areas: params.areas ?? [],
        },
        requestHeaders: options.headers,
        signal: options.signal,
      },
    )
    return data.restCocinaNotificaciones
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida las notificaciones de cocina desde cualquier lugar, sin necesidad de tener montado el hook.
 * @example invalidateComandasCocinaNotificacion()
 * @example invalidateComandasCocinaNotificacion({ extraQueryKeys: [codigoSucursal] })
 */
export const invalidateComandasCocinaNotificacion = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_COMANDAS_COCINA_NOTIFICACION, options)

/**
 * Hook para consultar las notificaciones de cocina.
 * queryKey: [KEY_COMANDAS_COCINA_NOTIFICACION, codigoSucursal, codigoPuntoVenta, estados, areas, ...(options.queryKeys ?? [])]
 * El cursor no forma parte del queryKey: se envía en cada consulta con el valor actual de params,
 * así actualizarlo no crea una consulta nueva ni vacía los datos.
 * Pensado para polling: pasar `refetchInterval` en options y guardar `data.cursor` para la siguiente.
 * @param params Sucursal, cursor y filtros opcionales (estados, punto de venta, áreas).
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 */
export const useComandasCocinaNotificacion = (
  params: ApiComandasCocinaNotificacionParams,
  options: QueryOpts<ComandasCocinaNotificacionesProps> = {},
) =>
  useApiQuery(
    KEY_COMANDAS_COCINA_NOTIFICACION,
    (ctx) => apiComandasCocinaNotificacion(params, { signal: ctx.signal }),
    {
      ...options,
      enabled: options.enabled ?? true,
      queryKeys: [
        params.codigoSucursal,
        params.codigoPuntoVenta ?? null,
        params.estados ?? [],
        params.areas ?? [],
        ...(options.queryKeys ?? []),
      ],
    },
  )
